/**
 * The authoring API — where a dimension error and a forward reference are
 * COMPILE errors, not runtime ones.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * A FORWARD REFERENCE IS UNCONSTRUCTIBLE, NOT REJECTED
 * ══════════════════════════════════════════════════════════════════════════
 *
 * A step's arguments are {@link CoverageTerm} handles, and the ONLY way to
 * obtain a handle is to call a builder method that has ALREADY appended its
 * step. So at the moment you write `b.mul(x, y)`, every handle in scope refers
 * to a step that exists and precedes the one about to be created. There is no
 * expression that names a later step, because the value that would name it does
 * not exist yet.
 *
 * That is strictly stronger than validation. The schema REJECTS a forward
 * reference in hand-written JSON — it must, because stored JSON does not come
 * from this file. Authoring code cannot write one down at all.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * A DIMENSION ERROR IS A TYPE ERROR
 * ══════════════════════════════════════════════════════════════════════════
 *
 *     b.add(hoursTerm, headcountTerm)   // does not compile
 *     b.add(hoursTerm, hoursTerm)       // fine
 *     b.mul(hoursTerm, headcountTerm)   // fine, and typed TIME·PERSON
 *
 * `add`, `sub`, `min` and `max` take one type parameter used by every operand,
 * so unequal dimensions have nothing to unify to. `mul` and `div` compute the
 * result's exponents at the type level, so the dimension of a product is known
 * to the compiler and flows into the next call.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * SO IS A PERIOD-BASIS ERROR — and `NoInfer` is what makes it one
 * ══════════════════════════════════════════════════════════════════════════
 *
 *     b.add(hoursPerYearTerm, plainHoursTerm)   // does not compile
 *
 * A term now carries TWO phantom parameters: its dimension and its period basis
 * (`coverage-period.types.ts`). Both flow through `mul` and `div` by the same
 * exponent arithmetic, and both must agree under `add`/`sub`/`min`/`max`.
 *
 * The basis needs `NoInfer` on the trailing operands and the dimension does
 * not, and that asymmetry is MEASURED, not assumed. A dimension's candidates
 * are object types with no common supertype, so TypeScript picks the first and
 * the second fails to check. A basis's candidates are numeric literals under a
 * union constraint, and TypeScript infers `-1 | 0` — a perfectly good type that
 * both operands satisfy, so the mismatch compiles clean. `NoInfer` stops the
 * later operands being inference sites at all, which turns them back into
 * things that are CHECKED against the first. Without it this whole axis is a
 * comment.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHERE THE COMPILE-TIME GUARANTEE STOPS — read this before relying on it
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Three boundaries, all of them checked at runtime instead:
 *
 * 1. STORED JSON gets no compile-time checking whatsoever, by construction. A
 *    model read from the database has no literal types. `parseCoverageFormula`
 *    is the check there, and `evaluateCoverageFormula` is the check on the
 *    dimensions the model actually produces.
 * 2. A HANDLE FROM ANOTHER BUILDER is a type error only by luck — two builders
 *    have the same static type, so TypeScript cannot tell them apart. Every
 *    method therefore checks the handle's owner token by identity and THROWS
 *    {@link CoverageFormulaBuilderError} on a foreign or forged handle. This is
 *    the one place in the module that throws, and it is correct that it does:
 *    it is a programming error in authoring code, at construction time, not a
 *    data problem at evaluation time.
 * 3. EXPONENTS BEYOND ±6 exceed what the type-level arithmetic represents. The
 *    type-level sum becomes `never`, `mul` and `div` detect that and return
 *    {@link CoverageDimensionOverflow} — a type nothing accepts, so the call
 *    site fails to compile rather than silently widening. The runtime check in
 *    the evaluator (`EXPONENT_OUT_OF_RANGE`) covers the same ground for parsed
 *    JSON.
 *
 * Everything the builder produces is run through `parseCoverageFormula` before
 * it is returned, so the builder and hand-written JSON share ONE validator.
 * There is no second source of truth about what a valid model is.
 */
import type {
  AnyCoverageDim,
  CoverageDim,
  CoverageDimension,
  CoverageDimensionProduct,
  CoverageDimensionProductAll,
  CoverageDimensionQuotient,
  CoverageExponent,
  CoverageExponentDifference,
  CoverageExponentSum
} from "./coverage-dimension.types";
import {
  COVERAGE_MAX_STEP_ARGS,
  type CoverageFunction,
  type CoverageInput,
  type CoverageRef,
  type CoverageStep,
  parseCoverageFormula,
  type ValidatedCoverageFormula,
  withCoverageResultPeriodBasis
} from "./coverage-formula.schema";
import {
  COVERAGE_PERIOD_BASIS_ABSOLUTE,
  COVERAGE_PERIOD_BASIS_ANNUAL,
  type CoveragePeriodBasis
} from "./coverage-period.types";
import type { CoverageTimeUnit } from "./coverage-time-unit.types";

/**
 * A value available at some point in a formula under construction, carrying its
 * dimension in the type system.
 *
 * `__coverageDimension` is phantom: declared, never assigned, never read. It
 * exists so `CoverageTerm<HOURS>` and `CoverageTerm<PEOPLE>` are different
 * types. It is optional so the builder can construct a handle without
 * fabricating a value for it; that costs nothing, because two concrete
 * dimensions still fail to unify — `{ time: 1 }` is not assignable to
 * `{ time: 0 }` whether the property is optional or not.
 */
export interface CoverageTerm<
  D extends AnyCoverageDim,
  B extends CoveragePeriodBasis = typeof COVERAGE_PERIOD_BASIS_ABSOLUTE
> {
  readonly __coverageDimension?: D;
  /**
   * Phantom period basis, for the same reason and by the same mechanism as
   * `__coverageDimension`. Defaults to ABSOLUTE so a bare `CoverageTerm<D>`
   * still names exactly the terms it named before this axis existed.
   */
  readonly __coveragePeriodBasis?: B;
  /** How the evaluator will name this value. */
  readonly ref: CoverageRef;
  /** The same dimension as `D`, in the form the runtime algebra reads. */
  readonly dimension: CoverageDimension;
  /** The same basis as `B`, in the form the runtime algebra reads. */
  readonly periodBasis: number;
  /** Identity of the builder that minted this handle. Checked, never trusted. */
  readonly owner: object;
}

/** A term of any dimension and any basis — the shape the erased implementation has. */
type AnyCoverageTerm = CoverageTerm<AnyCoverageDim, CoveragePeriodBasis>;

/**
 * What `mul` or `div` returns when the exponents leave the representable range.
 *
 * Assignable to nothing useful, so the call site fails to compile and the
 * message names the two dimensions that produced it.
 */
export interface CoverageDimensionOverflow<A, B> {
  readonly __coverageDimensionOverflow: "exponent outside [-6, +6]";
  readonly left: A;
  readonly right: B;
}

/**
 * The basis of a left-to-right product of a tuple of bases. `never` on overflow.
 *
 * The exact shape of `CoverageDimensionProductAll`, over one axis instead of
 * four, and built from the same `CoverageExponentSum` — this axis adds no new
 * type-level arithmetic.
 */
type PeriodBasisSumAll<Bs extends readonly CoveragePeriodBasis[]> = Bs extends readonly [
  infer Head extends CoveragePeriodBasis,
  ...infer Rest extends readonly CoveragePeriodBasis[]
]
  ? Rest extends readonly []
    ? Head
    : PeriodBasisSumAll<Rest> extends infer Tail
      ? [Tail] extends [never]
        ? never
        : Tail extends CoveragePeriodBasis
          ? CoverageExponentSum<Head, Tail>
          : never
      : never
  : typeof COVERAGE_PERIOD_BASIS_ABSOLUTE;

/**
 * A term is only produced when BOTH exponent vectors stayed representable.
 *
 * The dimension and the basis are checked in one place so neither can overflow
 * while the other is asked about — a hole of exactly the shape review found in
 * the first version of this module's zero check.
 */
type TermOrOverflow<A, B, D, Basis> = [D] extends [never]
  ? CoverageDimensionOverflow<A, B>
  : [Basis] extends [never]
    ? CoverageDimensionOverflow<A, B>
    : D extends AnyCoverageDim
      ? Basis extends CoveragePeriodBasis
        ? CoverageTerm<D, Basis>
        : never
      : never;

type ProductTerm<
  A extends AnyCoverageDim,
  B extends AnyCoverageDim,
  Ba extends CoveragePeriodBasis,
  Bb extends CoveragePeriodBasis
> = TermOrOverflow<A, B, CoverageDimensionProduct<A, B>, CoverageExponentSum<Ba, Bb>>;

type QuotientTerm<
  A extends AnyCoverageDim,
  B extends AnyCoverageDim,
  Ba extends CoveragePeriodBasis,
  Bb extends CoveragePeriodBasis
> = TermOrOverflow<A, B, CoverageDimensionQuotient<A, B>, CoverageExponentDifference<Ba, Bb>>;

type ProductAllTerm<
  Ds extends readonly AnyCoverageDim[],
  Bs extends readonly CoveragePeriodBasis[]
> = TermOrOverflow<Ds, Ds, CoverageDimensionProductAll<Ds>, PeriodBasisSumAll<Bs>>;

/** The dimensions of a tuple of terms, position by position. */
type DimensionsOfTerms<Ts extends readonly AnyCoverageTerm[]> = {
  readonly [K in keyof Ts]: Ts[K] extends CoverageTerm<infer D, CoveragePeriodBasis> ? D : never;
};

/** The period bases of a tuple of terms, position by position. */
type BasesOfTerms<Ts extends readonly AnyCoverageTerm[]> = {
  readonly [K in keyof Ts]: Ts[K] extends CoverageTerm<AnyCoverageDim, infer B> ? B : never;
};

/** An input as it is declared to the builder. The dimension keeps its literal type. */
export interface CoverageInputSpec<
  T extends CoverageExponent,
  P extends CoverageExponent,
  E extends CoverageExponent,
  M extends CoverageExponent,
  Per extends CoverageTimeUnit | undefined = undefined
> {
  /** Stable identifier. Must match `/^[a-z][a-zA-Z0-9_]{0,63}$/`. */
  readonly key: string;
  /** The authored magnitude, in `timeUnit`, counted over `perPeriod`. */
  readonly value: number;
  readonly dimension: CoverageDim<T, P, E, M>;
  /** Defaults to `hour`. Only meaningful when the dimension has a TIME exponent. */
  readonly timeUnit?: CoverageTimeUnit;
  /**
   * The window this magnitude accumulates over — `40 tickets per WEEK`.
   *
   * Declaring it annualises the magnitude at binding and types the term ANNUAL,
   * so the model needs no `weeksPerYear` factor and cannot be divided by an
   * absolute quantity. Omitting it leaves the term ABSOLUTE, which is right for
   * a stock (`12 people`), a factor (`0.8`) or a per-item rate
   * (`0.2 h/ticket`).
   */
  readonly perPeriod?: Per;
  readonly label?: string;
}

/**
 * The basis a declared input produces: ANNUAL when it names a period, ABSOLUTE
 * otherwise.
 *
 * An input is the ONLY place a basis enters a model. Every other basis in a
 * formula is computed from these by `mul` and `div`, which is what keeps the
 * authoring surface to one optional field.
 */
type BasisOfSpec<Per extends CoverageTimeUnit | undefined> = [Per] extends [CoverageTimeUnit]
  ? typeof COVERAGE_PERIOD_BASIS_ANNUAL
  : typeof COVERAGE_PERIOD_BASIS_ABSOLUTE;

/** Thrown only for a programming error in authoring code. Never for data. */
export class CoverageFormulaBuilderError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CoverageFormulaBuilderError";
  }
}

/** The methods available while a formula is being built. */
export interface CoverageFormulaBuilder {
  /** Declare an authored input and get a handle to it. */
  input<
    T extends CoverageExponent,
    P extends CoverageExponent,
    E extends CoverageExponent,
    M extends CoverageExponent,
    Per extends CoverageTimeUnit | undefined = undefined
  >(
    spec: CoverageInputSpec<T, P, E, M, Per>
  ): CoverageTerm<CoverageDim<T, P, E, M>, BasisOfSpec<Per>>;

  /** Sum values of one dimension AND one period basis. Mixing either does not compile. */
  add<D extends AnyCoverageDim, B extends CoveragePeriodBasis>(
    first: CoverageTerm<D, B>,
    second: CoverageTerm<D, NoInfer<B>>,
    ...rest: readonly CoverageTerm<D, NoInfer<B>>[]
  ): CoverageTerm<D, B>;

  /** Difference of two values of one dimension and one period basis. */
  sub<D extends AnyCoverageDim, B extends CoveragePeriodBasis>(
    left: CoverageTerm<D, B>,
    right: CoverageTerm<D, NoInfer<B>>
  ): CoverageTerm<D, B>;

  /** Smallest of values of one dimension and one period basis. */
  min<D extends AnyCoverageDim, B extends CoveragePeriodBasis>(
    first: CoverageTerm<D, B>,
    second: CoverageTerm<D, NoInfer<B>>,
    ...rest: readonly CoverageTerm<D, NoInfer<B>>[]
  ): CoverageTerm<D, B>;

  /** Largest of values of one dimension and one period basis. */
  max<D extends AnyCoverageDim, B extends CoveragePeriodBasis>(
    first: CoverageTerm<D, B>,
    second: CoverageTerm<D, NoInfer<B>>,
    ...rest: readonly CoverageTerm<D, NoInfer<B>>[]
  ): CoverageTerm<D, B>;

  /** Product of two values. Dimension and basis are both computed at compile time. */
  mul<
    A extends AnyCoverageDim,
    B extends AnyCoverageDim,
    Ba extends CoveragePeriodBasis,
    Bb extends CoveragePeriodBasis
  >(
    left: CoverageTerm<A, Ba>,
    right: CoverageTerm<B, Bb>
  ): ProductTerm<A, B, Ba, Bb>;

  /**
   * Product of three or more values, as ONE step. Dimensions and bases multiply
   * in order.
   *
   * Generic over the TERMS rather than over their dimensions, so the dimension
   * and the basis of each position are recovered together by `infer` and cannot
   * drift out of step — two parallel tuple parameters would let a caller supply
   * a basis tuple that does not line up with the dimension one.
   */
  mulAll<Ts extends readonly [AnyCoverageTerm, AnyCoverageTerm, ...AnyCoverageTerm[]]>(
    ...terms: Ts
  ): ProductAllTerm<DimensionsOfTerms<Ts>, BasesOfTerms<Ts>>;

  /** Quotient of two values. Dimension and basis are both computed at compile time. */
  div<
    A extends AnyCoverageDim,
    B extends AnyCoverageDim,
    Ba extends CoveragePeriodBasis,
    Bb extends CoveragePeriodBasis
  >(
    numerator: CoverageTerm<A, Ba>,
    denominator: CoverageTerm<B, Bb>
  ): QuotientTerm<A, B, Ba, Bb>;
}

/** The builder with its dimensions erased — the shape the implementation has. */
interface ErasedCoverageFormulaBuilder {
  input(spec: ErasedInputSpec): AnyCoverageTerm;
  add(...terms: readonly AnyCoverageTerm[]): AnyCoverageTerm;
  sub(left: AnyCoverageTerm, right: AnyCoverageTerm): AnyCoverageTerm;
  min(...terms: readonly AnyCoverageTerm[]): AnyCoverageTerm;
  max(...terms: readonly AnyCoverageTerm[]): AnyCoverageTerm;
  mul(left: AnyCoverageTerm, right: AnyCoverageTerm): AnyCoverageTerm;
  mulAll(...terms: readonly AnyCoverageTerm[]): AnyCoverageTerm;
  div(numerator: AnyCoverageTerm, denominator: AnyCoverageTerm): AnyCoverageTerm;
}

interface ErasedInputSpec {
  readonly key: string;
  readonly value: number;
  readonly dimension: CoverageDimension;
  readonly timeUnit?: CoverageTimeUnit;
  readonly perPeriod?: CoverageTimeUnit;
  readonly label?: string;
}

class BuilderCore {
  /** Identity, not a value. Two builders are distinguished by `===` on this. */
  private readonly token: object = {};

  private readonly inputs: CoverageInput[] = [];

  private readonly steps: CoverageStep[] = [];

  private sealed = false;

  readonly api: CoverageFormulaBuilder;

  constructor() {
    const erased: ErasedCoverageFormulaBuilder = {
      input: (spec) => this.declareInput(spec),
      add: (first, second, ...rest) => this.sameDimensionStep("add", [first, second, ...rest]),
      sub: (left, right) => this.sameDimensionStep("sub", [left, right]),
      min: (first, second, ...rest) => this.sameDimensionStep("min", [first, second, ...rest]),
      max: (first, second, ...rest) => this.sameDimensionStep("max", [first, second, ...rest]),
      mul: (left, right) => this.productStep([left, right]),
      mulAll: (...terms) => this.productStep(terms),
      div: (numerator, denominator) => this.quotientStep(numerator, denominator)
    };

    // The one assertion in this file, and it is the type-erasure seam that
    // every phantom-typed builder has: the METHODS are generic over dimensions,
    // the IMPLEMENTATIONS work on the runtime dimension vector and cannot be,
    // because the exponents exist only in the type system. What is asserted is
    // exactly what `coverage-formula.builder.test.ts` pins — that the runtime
    // vector each method computes agrees with the type-level dimension its
    // signature declares. `ErasedCoverageFormulaBuilder` exists so the object
    // above is still fully checked against the erased shape rather than being
    // written straight into an `as unknown as`.
    this.api = erased as unknown as CoverageFormulaBuilder;
  }

  seal<B extends CoveragePeriodBasis>(
    result: CoverageTerm<AnyCoverageDim, B>
  ): ValidatedCoverageFormula<B> {
    this.assertOwned(result);
    this.sealed = true;

    const parsed = parseCoverageFormula({
      version: 1,
      inputs: this.inputs,
      steps: this.steps,
      result: result.ref
    });

    if (!parsed.ok) {
      // Unreachable unless this file has a bug: every path above appends a step
      // whose arguments already exist. Surfaced loudly rather than swallowed,
      // because a silent disagreement between the builder and the schema is the
      // exact drift the shared validator exists to prevent.
      const detail = parsed.error.issues
        .map((issue) => `${issue.path}: ${issue.message}`)
        .join("; ");
      throw new CoverageFormulaBuilderError(
        `builder produced a formula its own schema rejects — ${detail}`
      );
    }

    // The basis is the one this builder computed while appending the steps, so
    // the label states what is already true rather than asserting something new.
    return withCoverageResultPeriodBasis<B>(parsed.value);
  }

  private declareInput(spec: ErasedInputSpec): AnyCoverageTerm {
    this.assertOpen();
    if (this.inputs.some((existing) => existing.key === spec.key)) {
      throw new CoverageFormulaBuilderError(`duplicate input key "${spec.key}"`);
    }

    this.inputs.push({
      key: spec.key,
      value: spec.value,
      dimension: {
        time: spec.dimension.time,
        person: spec.dimension.person,
        event: spec.dimension.event,
        money: spec.dimension.money
      },
      ...(spec.timeUnit === undefined ? {} : { timeUnit: spec.timeUnit }),
      ...(spec.perPeriod === undefined ? {} : { perPeriod: spec.perPeriod }),
      ...(spec.label === undefined ? {} : { label: spec.label })
    });

    return {
      ref: { kind: "input", key: spec.key },
      dimension: spec.dimension,
      // The runtime half of `BasisOfSpec`. The evaluator derives the same value
      // from the stored `perPeriod`, and `coverage-formula.builder.test.ts`
      // pins the two against each other — the builder claiming one basis while
      // evaluation computes another is the drift this whole seam risks.
      periodBasis:
        spec.perPeriod === undefined
          ? COVERAGE_PERIOD_BASIS_ABSOLUTE
          : COVERAGE_PERIOD_BASIS_ANNUAL,
      owner: this.token
    };
  }

  private sameDimensionStep(
    fn: Extract<CoverageFunction, "add" | "sub" | "min" | "max">,
    terms: readonly AnyCoverageTerm[]
  ): AnyCoverageTerm {
    return this.appendStep(fn, terms, terms[0].dimension, terms[0].periodBasis);
  }

  private productStep(terms: readonly AnyCoverageTerm[]): AnyCoverageTerm {
    const dimension = terms.reduce<CoverageDimension>(
      (accumulator, term) => ({
        time: accumulator.time + term.dimension.time,
        person: accumulator.person + term.dimension.person,
        event: accumulator.event + term.dimension.event,
        money: accumulator.money + term.dimension.money
      }),
      { time: 0, person: 0, event: 0, money: 0 }
    );
    const periodBasis = terms.reduce(
      (accumulator, term) => accumulator + term.periodBasis,
      COVERAGE_PERIOD_BASIS_ABSOLUTE
    );
    return this.appendStep("mul", terms, dimension, periodBasis);
  }

  private quotientStep(numerator: AnyCoverageTerm, denominator: AnyCoverageTerm): AnyCoverageTerm {
    const dimension: CoverageDimension = {
      time: numerator.dimension.time - denominator.dimension.time,
      person: numerator.dimension.person - denominator.dimension.person,
      event: numerator.dimension.event - denominator.dimension.event,
      money: numerator.dimension.money - denominator.dimension.money
    };
    return this.appendStep(
      "div",
      [numerator, denominator],
      dimension,
      numerator.periodBasis - denominator.periodBasis
    );
  }

  /**
   * Append a step and hand back a handle to it.
   *
   * The order here IS the forward-reference guarantee: the step is pushed
   * first, and the handle it returns names the position it was pushed to. A
   * caller can only ever reference what this method has already appended.
   */
  private appendStep(
    fn: CoverageFunction,
    terms: readonly AnyCoverageTerm[],
    dimension: CoverageDimension,
    periodBasis: number
  ): AnyCoverageTerm {
    this.assertOpen();
    if (terms.length > COVERAGE_MAX_STEP_ARGS) {
      throw new CoverageFormulaBuilderError(
        `"${fn}" was given ${terms.length} arguments; the maximum is ${COVERAGE_MAX_STEP_ARGS}`
      );
    }
    for (const term of terms) {
      this.assertOwned(term);
    }

    const index = this.steps.length;
    this.steps.push({ fn, args: terms.map((term) => term.ref) });

    return { ref: { kind: "step", index }, dimension, periodBasis, owner: this.token };
  }

  private assertOpen(): void {
    if (this.sealed) {
      throw new CoverageFormulaBuilderError(
        "this formula is already built — a builder cannot be reused after its callback returns"
      );
    }
  }

  /**
   * Refuse a handle this builder did not mint.
   *
   * TypeScript cannot separate two `CoverageFormulaBuilder`s, so this is the
   * only thing standing between a handle borrowed from another formula and a
   * step that references an index meaning something else entirely.
   */
  private assertOwned(term: AnyCoverageTerm): void {
    if (term === null || typeof term !== "object" || term.owner !== this.token) {
      throw new CoverageFormulaBuilderError(
        "term does not belong to this formula — a handle from another builder cannot be used here"
      );
    }
  }
}

/**
 * Build a formula, and validate it through the same door hand-written JSON uses.
 *
 * The callback returns the term the model produces; that term becomes `result`.
 * The builder is sealed the moment the callback returns, so a handle captured
 * out of the closure cannot be used to keep appending to a finished model.
 *
 * The PERIOD BASIS of the term the callback returns survives into the returned
 * type, which is what lets `computeRoleCoverage` refuse a per-year denominator
 * under an absolute numerator before the code runs. The dimension deliberately
 * does NOT survive: the wrong-dimension case is already caught at runtime, with
 * a named warning and tests, and promoting it to a compile error would refuse
 * the very models those tests build to prove it.
 *
 * @throws {CoverageFormulaBuilderError} on a foreign handle, a sealed builder,
 * or — a bug in this file, not in the caller — a model that fails its own
 * schema.
 */
export function buildCoverageFormula<B extends CoveragePeriodBasis>(
  define: (builder: CoverageFormulaBuilder) => CoverageTerm<AnyCoverageDim, B>
): ValidatedCoverageFormula<B> {
  const core = new BuilderCore();
  const result = define(core.api);
  return core.seal(result);
}
